import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { z } from 'zod';
import { useOwnProfile } from '_api/useProfile';
import { useAppNavigation } from '_navigator';
import { track } from '_utils/Amplitude';
import useIsHelpfulNeighbor from '_utils/messages/useIsHelpfulNeighbor';
import safeParse from '_utils/safeParse';
import { screenMargin } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { HeaderWithNav } from 'Components';
import ActionBanner from 'Components/ActionBanner';
import { MessageType, HelpfulNeighborTags, isHelpfulNeighborMessage } from 'types/message';
import { RoutePropsSchema } from 'types/Utility';
import { useSearchMessages } from './common';
import MessageListView from './common/MessageListView';
import useMarkMessagesRead from './common/useMarkMessagesRead';
import useSharingContextMenuEffect from './common/useSharingContextMenuEffect';

const routeParams = RoutePropsSchema(
    z.object({
        category: z.enum(HelpfulNeighborTags),
    }),
);
const HelpfulNeighborCategorySelected = (params: z.infer<typeof routeParams>): ReactElement => {
    const { category } = useMemo(() => safeParse(params, routeParams), [params]).route.params;
    const { t } = useTranslation();
    const { navigate } = useAppNavigation();
    const { data: ownProfile } = useOwnProfile();
    const isHelpfulNeighbor = useIsHelpfulNeighbor(ownProfile?.id ?? 0);
    const { theme } = useThemeContext();
    const [search, status, queryViewProps, messages, hasSearched] = useSearchMessages([MessageType.Helpers]);
    const markMessages = useMarkMessagesRead();

    const handleBecomeHelpfulNeighbor = () => {
        track('Pressed become helpful neighbor banner in Helpful Neighbor category selected overview');
        navigate('SharingNew', { type: MessageType.Helpers });
    };
    useSharingContextMenuEffect([MessageType.Helpers]);

    const selectedMessages = useMemo(() => {
        return messages
            .filter(isHelpfulNeighborMessage)
            .filter((message) => message.helpful_neighbor_tags.includes(category));
    }, [category, messages]);

    return (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.mainBackground,
                height: '100%',
            }}
        >
            <HeaderWithNav title={t(`sharingAll:${category}_title`)} safeArea />
            <MessageListView
                parent="helpfulNeighbors"
                {...markMessages}
                {...queryViewProps}
                top={
                    <>
                        {isHelpfulNeighbor ? undefined : (
                            <ActionBanner
                                onPress={handleBecomeHelpfulNeighbor}
                                title={t('sharingAll:becomeHelpfulNeighbor')}
                                message={t('sharingAll:becomeHelpfulNeighborDescription_selected_category', {
                                    category: t(`sharingAll:${category}_title`),
                                })}
                                icon="users"
                                variant="GreenLight"
                                containerStyle={{ marginTop: screenMargin }}
                            />
                        )}
                        {search}
                    </>
                }
                numColumns={2}
                data={selectedMessages}
                isRefreshing={status === 'refetching'}
                isSearching={status === 'searching'}
                emptyText={hasSearched ? t('sharingAll:noSearchResult') : t('sharingAll:noMessageType:Helpers')}
                showActionButton={!hasSearched}
                preferableCategories={[category]}
            />
        </View>
    );
};

export default HelpfulNeighborCategorySelected;
